import { BlockStack, Card, InlineStack, Text } from '@shopify/polaris'
import type { BccData } from './useBccData'
import { CardHeader } from './LensBadge'

// The order journey, every stage counted from the sheet rather than estimated.
//
// The Shipped stage used to be derived from a formula of my own invention, with
// hardcoded fallbacks (74% confirmation, 79% delivery, 21% returns) standing in
// whenever data was missing. A business owner reading an invented number takes a
// real decision on it, which is worse than showing nothing — so the fallbacks are
// gone and every stage now comes from computeOrderTypeBreakdown (§9.9).

function pct(n: number | null): string {
  if (n == null) return '—'
  return `${n.toFixed(1)}%`
}

type StageProps = {
  label:       string
  count:       number
  conversion?: number | null   // % of the stage above
  note?:       string
  isFirst?:    boolean
  isLast?:     boolean
  tone?:       'success' | 'critical' | 'caution'
}

function FunnelStage({ label, count, conversion, note, isFirst, isLast, tone }: StageProps) {
  const convTone =
    conversion == null ? undefined :
    conversion >= 85   ? 'success' :
    conversion >= 70   ? 'caution' : 'critical'

  return (
    <BlockStack gap="0">
      <div style={{
        background:      'var(--p-color-bg-surface-secondary)',
        border:          '1px solid var(--p-color-border)',
        borderRadius:    8,
        padding:         '10px 14px',
        display:         'flex',
        alignItems:      'center',
        justifyContent:  'space-between',
      }}>
        <BlockStack gap="025">
          <Text as="p" variant="bodyMd" fontWeight="semibold">{label}</Text>
          {note && <Text as="p" variant="bodySm" tone="subdued">{note}</Text>}
        </BlockStack>
        <InlineStack gap="300" blockAlign="center">
          {!isFirst && conversion != null && (
            <Text as="p" variant="bodySm" tone={convTone}>{pct(conversion)}</Text>
          )}
          <Text
            as="p"
            variant="bodyMd"
            fontWeight="semibold"
            tone={tone === 'critical' ? 'critical' : tone === 'success' ? 'success' : undefined}
          >
            {count.toLocaleString('en-US')}
          </Text>
        </InlineStack>
      </div>

      {!isLast && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '1px 0' }}>
          <div style={{ width: 1, height: 12, background: 'var(--p-color-border)' }} />
        </div>
      )}
    </BlockStack>
  )
}

type Props = { data: BccData }

export function OperationalFunnel({ data }: Props) {
  const { dashboard } = data

  // Normal orders only. Exchanges and return pickups never pass through phone
  // confirmation, so folding them in divided confirmed orders by a population
  // that includes types which can never be confirmed — reading the confirmation
  // rate several points below the truth. Their journey is a different one and is
  // covered in full by the per-order-type table.
  const t = dashboard.orderTypes.types.find(x => x.orderType === 'normal')

  const received     = t?.total            ?? 0
  const confirmed    = t?.confirmed        ?? 0
  const shipApproved = t?.shipConfirmed    ?? 0
  const dispatched   = t?.dispatched       ?? 0
  const succeeded    = t?.succeeded.count  ?? 0
  const failed       = t?.failed.count     ?? 0

  const otherTypes = dashboard.orderTypes.totalOrders - received

  // Conversion is always against the stage directly above. A stage with no
  // recorded activity yields null and renders as "—" rather than a guess.
  const conv = (n: number, prev: number): number | null => (prev > 0 ? (n / prev) * 100 : null)

  const inTransit = dispatched - succeeded - failed

  return (
    <Card>
      <BlockStack gap="400">
        {/* Maturity for delivery orders specifically — the global figure covers
            every non-return order, a different population from this card. */}
        <CardHeader
          title="Operational Funnel — Delivery Orders"
          lens="performance"
          maturity={t
            ? {
                month:          dashboard.maturity.month,
                totalOrders:    t.total,
                resolvedOrders: t.resolved,
                pendingOrders:  t.total - t.resolved,
                maturityPct:    t.maturityPct,
                isFinal:        t.isFinal,
              }
            : dashboard.maturity}
        />

        <BlockStack gap="0">
          <FunnelStage
            label="Received"
            count={received}
            isFirst
          />
          <FunnelStage
            label="Confirmed"
            note="Confirmed with the customer by phone"
            count={confirmed}
            conversion={conv(confirmed, received)}
          />
          <FunnelStage
            label="Ship-approved"
            note="Re-confirmed after stock was reserved"
            count={shipApproved}
            conversion={conv(shipApproved, confirmed)}
          />
          <FunnelStage
            label="Dispatched"
            note="Left the warehouse with a tracking number"
            count={dispatched}
            conversion={conv(dispatched, shipApproved)}
          />
          <FunnelStage
            label="Succeeded"
            note="Courier completed the delivery"
            count={succeeded}
            conversion={conv(succeeded, dispatched)}
            tone="success"
          />
          <FunnelStage
            label="Failed"
            note="Returned to the warehouse"
            count={failed}
            conversion={conv(failed, dispatched)}
            tone={failed > 0 && conv(failed, dispatched)! > 25 ? 'critical' : undefined}
            isLast
          />
        </BlockStack>

        <BlockStack gap="100">
          {inTransit > 0 && (
            <Text as="p" variant="bodySm" tone="subdued">
              {`${inTransit.toLocaleString('en-US')} dispatched ${inTransit === 1 ? 'order is' : 'orders are'} still in transit — their outcome is not counted above yet.`}
            </Text>
          )}
          {otherTypes > 0 && (
            <Text as="p" variant="bodySm" tone="subdued">
              {`${otherTypes.toLocaleString('en-US')} exchange and return ${otherTypes === 1 ? 'order follows' : 'orders follow'} a different journey — see the table above.`}
            </Text>
          )}
        </BlockStack>
      </BlockStack>
    </Card>
  )
}
